import {createApi, retry} from '@reduxjs/toolkit/query/react';
import _ from 'lodash';
import axios from 'axios';
import store from '../store';
import {getSharedDocumentHeader} from './apiUtils';
import {selectEffectiveOrg} from '../data/session';
import {isRetryableError} from '../util/apiErrors';

const axiosBaseQuery =
  ({baseUrl} = {baseUrl: ''}) =>
  async ({url, method = 'GET', data, params, headers}) => {
    const state = store.getState();

    const {session} = state;
    const effectiveOrg = selectEffectiveOrg(state);

    const _headers = {
      'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
      ...getSharedDocumentHeader(),
      ...headers,
    };
    if (!_.isNil(session.accessToken)) {
      _headers.Authorization = `Bearer ${session.accessToken}`;
    }
    try {
      return await axios({
        url: `${baseUrl}${url}`,
        method,
        data,
        params,
        headers: _headers,
      });
    } catch (axiosError) {
      const err = axiosError;
      const error = {
        code: err.code,
        status: err.response?.status,
        data: err.response?.data || err.message,
      };
      if (!isRetryableError(error)) {
        retry.fail(error);
      }
      return {error};
    }
  };

export const copilotV2ApiSlice = createApi({
  reducerPath: 'copilotV2Api',
  tagTypes: ['Conversations'],
  baseQuery: retry(
    axiosBaseQuery({baseUrl: process.env.REACT_APP_COPILOT_V2_API_URL}),
    {
      maxRetries: 3,
    }
  ),
  endpoints: (builder) => ({
    /*********************************************
     * CONVERSATIONS (Agent Chat / Penny)
     *********************************************/
    listConversations: builder.query({
      query: (params = {}) => ({
        url: '/api/v1/conversations',
        method: 'GET',
        params: {
          limit: params.limit || 10,
          offset: params.offset || 0,
          ...(params.status && {status: params.status}),
          ...(params.document_id && {document_id: params.document_id}),
        },
      }),
      transformResponse: (response) => response.data ?? response,
      providesTags: ['Conversations'],
    }),
    createConversation: builder.mutation({
      query: ({title = 'New Chat', documentId}) => ({
        url: '/api/v1/conversations',
        method: 'POST',
        data: {title, document_id: documentId},
      }),
      transformResponse: (response) => {
        const data = response.data ?? response;
        if (data && !data.created_at) {
          data.created_at = new Date().toISOString();
        }
        return data;
      },
      invalidatesTags: ['Conversations'],
    }),
    getConversation: builder.query({
      query: ({conversationId}) => ({
        url: `/api/v1/conversations/${conversationId}`,
        method: 'GET',
      }),
      transformResponse: (response) => response.data ?? response,
    }),
    getMessages: builder.query({
      query: ({conversationId, ...params}) => ({
        url: `/api/v1/conversations/${conversationId}/messages`,
        method: 'GET',
        params,
      }),
      transformResponse: (response) => response.data ?? response,
    }),
    updateConversation: builder.mutation({
      query: ({conversationId, ...data}) => ({
        url: `/api/v1/conversations/${conversationId}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['Conversations'],
    }),
    deleteConversation: builder.mutation({
      query: ({conversationId}) => ({
        url: `/api/v1/conversations/${conversationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Conversations'],
    }),
    checkDocument: builder.mutation({
      query: ({documentId, checkType}) => ({
        url: `/suggestions/${checkType}/${documentId}`,
        method: 'POST',
      }),
    }),
    dismissDiscrepancy: builder.mutation({
      query: ({data}) => ({
        url: '/suggestions/dismiss',
        method: 'POST',
        data,
      }),
    }),
    getJobStatus: builder.query({
      query: ({jobId, checkType}) => ({
        url: `/suggestions/${jobId}/${checkType}`,
        method: 'GET',
      }),
    }),
    /*********************************************
     * FLUX ANALYSIS
     *********************************************/

    // Used by backend for excel generation
    // GET    /api/v1/flux-analysis/{report_type}/{period}/{compare_period}

    // Returns a pre-existing analysis, if possible otherwise create new
    // Is not used for percent or absolute number values, that is the GET endpoint for /changes
    // POST   /api/v1/flux-analysis/{report_type}/{period}/{compare_period}
    getOrCreateFluxAnalysis: builder.mutation({
      query: ({reportType, period, comparePeriod, rounding}) => ({
        url: `/api/v1/flux-analysis/${reportType}/${period}/${comparePeriod}`,
        method: 'POST',
        params: {
          rounding, // ones|thousands|millions
        },
      }),
    }),

    // GET /api/v1/flux-analysis/{report_type}/{period}/{compare_period}/changes
    getFluxAnalysisChanges: builder.query({
      query: ({reportType, period, comparePeriod, rounding}) => ({
        url: `/api/v1/flux-analysis/${reportType}/${period}/${comparePeriod}/changes`,
        method: 'GET',
        params: {
          rounding, // ones|thousands|millions
        },
      }),
    }),

    // DELETE  /api/v1/flux-analysis/{report_type}/{period}/{compare_period}

    // POST    /api/v1/flux-analysis/{report_type}/{period}/{compare_period}/{pli_id}/edit
    editFluxAnalysis: builder.mutation({
      query: ({
        reportType,
        period,
        comparePeriod,
        pliId,
        editType, // condense, try_again, rewrite, or add_more_detail
        currentAnalysis,
      }) => ({
        url: `/api/v1/flux-analysis/${reportType}/${period}/${comparePeriod}/${pliId}/edit`,
        method: 'POST',
        data: {
          edit_type: editType,
          current_analysis: currentAnalysis,
        },
      }),
    }),

    // PATCH   /api/v1/flux-analysis/{report_type}/{period}/{compare_period}/{pli_id}
    patchFluxAnalysis: builder.mutation({
      query: ({reportType, period, comparePeriod, pliId, analysis}) => ({
        url: `/api/v1/flux-analysis/${reportType}/${period}/${comparePeriod}/${pliId}`,
        method: 'PATCH',
        data: {
          analysis,
        },
      }),
    }),

    // POST    /api/v1/flux-analysis/{report_type}/{period}/{compare_period}/{pli_id}/undo (FUTURE TODO)

    /*********************************************
     * TABLE FORMATTING
     *********************************************/
    formatTable: builder.mutation({
      query: ({tableId, tableData, tableMeta, documentId}) => ({
        url: '/api/v1/table/format_table',
        method: 'POST',
        data: {
          id: tableId,
          table_data: JSON.stringify(tableData),
          table_meta: JSON.stringify(tableMeta),
          document_id: documentId,
        },
      }),
    }),
    getTableFormattingSuggestions: builder.mutation({
      query: ({tableId, tableData, tableMeta, documentId}) => ({
        url: '/api/v1/table/format_table',
        method: 'POST',
        data: {
          id: tableId,
          table_data: JSON.stringify(tableData),
          table_meta: JSON.stringify(tableMeta),
          document_id: documentId,
        },
      }),
    }),
  }),
});

export const {
  endpoints,
  useCheckDocumentMutation,
  useDismissDiscrepancyMutation,
  useLazyGetJobStatusQuery,
  // Flux Analysis
  useGetOrCreateFluxAnalysisMutation,
  useDeleteFluxAnalysisMutation,
  useEditFluxAnalysisMutation,
  usePatchFluxAnalysisMutation,
  useLazyGetFluxAnalysisChangesQuery,
  // Table Formatting
  useFormatTableMutation,
  useGetTableFormattingSuggestionsMutation,
  // Conversations (Agent Chat / Penny)
  useLazyListConversationsQuery,
  useCreateConversationMutation,
  useLazyGetConversationQuery,
  useLazyGetMessagesQuery,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
} = copilotV2ApiSlice;
